import * as React from "react";
import { avatarHueFromKey, color, radius } from "@tokens/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Text } from "./Text";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Avatar: a flat tinted circle.
 *
 * Without `src`, the initials from `alt` sit on a curated 12-hue OKLCH palette,
 * providing optimal contrast in both light and dark themes. `presence` adds a
 * small status dot in the corner when explicitly passed.
 */

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarPresence = "online" | "away" | "dnd" | "offline";

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
    src?: string;
    alt: string;
    size?: AvatarSize;
    /** Use the alt-derived hue as the fallback fill. Default `true`. */
    tinted?: boolean;
    presence?: AvatarPresence;
}

const sizePx: Record<AvatarSize, number> = {
    xs: 20,
    sm: 24,
    md: 32,
    lg: 40,
    xl: 56,
};

const presenceColor: Record<AvatarPresence, string> = {
    online: color.status.successFg,
    away: color.status.warningFg,
    dnd: color.status.dangerFg,
    offline: color.fg.subtle,
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
    ({ src, alt, size = "md", tinted = true, presence, className, style, ...props }, ref) => {
        const initials = alt
            .split(" ")
            .map((n) => n[0])
            .filter((c): c is string => c !== undefined)
            .slice(0, 2)
            .join("")
            .toUpperCase();

        const px = sizePx[size];
        const hue = avatarHueFromKey(alt);
        const fill = tinted ? color.effect.avatarFallback : color.bg.surface3;

        const computed: React.CSSProperties & Record<"--avatar-hue", string> = {
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
            width: `${String(px)}px`,
            height: `${String(px)}px`,
            "--avatar-hue": String(hue),
            borderRadius: radius.full,
            background: fill,
            color: tinted ? color.accent.onPrimary : color.fg.default,
            border: `1px solid ${color.border.subtle}`,
            userSelect: "none",
        };

        const merged: React.CSSProperties =
            style !== undefined ? { ...computed, ...style } : computed;

        const dotPx = Math.max(6, Math.round(px * 0.28));
        const presenceDotStyle: React.CSSProperties | undefined =
            presence !== undefined
                ? {
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: `${String(dotPx)}px`,
                      height: `${String(dotPx)}px`,
                      background: presenceColor[presence],
                      borderRadius: radius.full,
                      border: `2px solid ${color.bg.canvas}`,
                      boxSizing: "border-box",
                  }
                : undefined;

        const initialsSize: "xs" | "sm" | "base" | "md" =
            size === "xs" || size === "sm"
                ? "xs"
                : size === "md"
                  ? "sm"
                  : size === "lg"
                    ? "base"
                    : "md";

        return (
            <div ref={ref} className={cn(className)} style={merged} {...props}>
                {src !== undefined && src.length > 0 ? (
                    <img
                        src={src}
                        alt={alt}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                ) : (
                    <Text
                        as="span"
                        role="caps"
                        size={initialsSize}
                        style={{
                            color: "inherit",
                            lineHeight: 1,
                            ...(size === "xs" ? { fontSize: "10px" } : size === "sm" ? { fontSize: "11px" } : {}),
                        }}
                    >
                        {initials}
                    </Text>
                )}
                {presenceDotStyle !== undefined && (
                    <span aria-hidden="true" style={presenceDotStyle} />
                )}
            </div>
        );
    },
);

Avatar.displayName = "Avatar";
